import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Heart, Users, Calendar, Target } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface FAQ {
  id: string;
  question: string;
  answer: string;
  display_order: number;
}

const defaultFAQs: FAQ[] = [
  {
    id: "1",
    question: "Do I get paid?",
    answer: "No, PTA members are volunteers. However, you'll gain valuable experience, make lasting friendships, and have the satisfaction of making a real difference in your child's school community.",
    display_order: 0
  },
  {
    id: "2", 
    question: "I'm concerned it will take up too much of my free time?",
    answer: "We understand everyone has busy lives! You can choose your level of involvement - from helping at occasional events to taking on a committee role. Even a few hours per term makes a difference.",
    display_order: 1
  },
  {
    id: "3",
    question: "Where do you meet?",
    answer: "We typically meet in the school staff room. Meeting times are arranged to suit the majority of members, and we also offer virtual attendance options when possible.",
    display_order: 2
  },
  {
    id: "4",
    question: "What if I just want to help out at events?",
    answer: "That's perfect! We have an 'Occasional Helper' role for parents who want to volunteer at specific events without the commitment of a committee position. Just let us know your availability.",
    display_order: 3
  }
];

export default function BecomeAMember() {
  const { ptaId } = useParams();
  const [ptaData, setPtaData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [faqs, setFaqs] = useState<FAQ[]>([]);

  useEffect(() => {
    fetchPTAData();
    fetchFAQs();
  }, [ptaId]);

  const fetchPTAData = async () => {
    setLoading(true);
    // Use RPC function to bypass RLS for anonymous users and get header_color
    const { data: pta } = await supabase
      .rpc('get_public_pta_info', { p_pta_id: ptaId || '' })
      .single();

    if (pta) {
      setPtaData(pta);
    }
    setLoading(false);
  };

  const fetchFAQs = async () => {
    if (!ptaId) return;
    
    const { data } = await supabase
      .from('pta_faqs')
      .select('*')
      .eq('pta_id', ptaId)
      .eq('is_active', true)
      .order('display_order', { ascending: true });
    
    if (data && data.length > 0) {
      setFaqs(data);
    } else {
      // Use default FAQs if none configured
      setFaqs(defaultFAQs);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header 
        className="sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border"
        style={{ backgroundColor: ptaData?.header_color ? `${ptaData.header_color}` : undefined }}
      >
        <div className="container flex h-20 items-center justify-between">
          <Link to={`/fundraising/${ptaId}`} className="flex items-center gap-2">
            {ptaData?.logo_url ? (
              <img src={ptaData.logo_url} alt="PTA Logo" className="h-12 w-auto rounded object-cover" />
            ) : (
              <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                <span className="text-lg">🏫</span>
              </div>
            )}
            <span className="font-semibold text-lg hidden sm:inline-block">
              {ptaData?.name || 'PTA'}
            </span>
          </Link>
          <Link to={`/fundraising/${ptaId}`}>
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Fundraising
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="container py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Join {ptaData?.name || 'Our PTA'}
            </h1>
            <p className="text-xl text-muted-foreground">
              Help us make a difference in our school community
            </p>
          </div>

          {/* Main Content Card */}
          <Card className="shadow-elevated mb-8">
            <CardHeader>
              <CardTitle className="text-2xl">How to Get Involved</CardTitle>
              <CardDescription>
                We welcome parents, guardians, and community members
              </CardDescription>
            </CardHeader>
            <CardContent className="prose prose-slate dark:prose-invert max-w-none">
              <p className="whitespace-pre-wrap text-foreground leading-relaxed">
                {ptaData?.volunteer_text || 
                  'Learn how you can get involved with our PTA and make a difference in our school community. We welcome parents, guardians, and community members who want to contribute their time and talents.'}
              </p>
            </CardContent>
          </Card>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card className="shadow-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>Build Community</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Connect with other parents and create lasting friendships while supporting your child's education.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Heart className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>Make an Impact</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Your involvement directly benefits the children and helps fund important school projects and activities.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>Flexible Commitment</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Choose how much time you can give - whether it's helping at events or joining the committee.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Target className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>Achieve Goals</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Work together towards shared goals that enhance the school experience for all children.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* FAQ Section */}
          <Card className="shadow-card mb-8">
            <CardHeader>
              <CardTitle className="text-2xl">Frequently Asked Questions</CardTitle>
              <CardDescription>
                Common questions about joining the PTA
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={faq.id} value={`faq-${index}`}>
                    <AccordionTrigger className="text-left font-medium">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          {/* Contact CTA */}
          <Card className="shadow-elevated bg-gradient-hero border-0">
            <CardContent className="pt-6 text-center">
              <h3 className="text-2xl font-bold mb-2">Ready to Join?</h3>
              <p className="text-muted-foreground mb-6">
                {ptaData?.shared_email 
                  ? `Get in touch with us at ${ptaData.shared_email} to learn more`
                  : 'Contact us to learn more about becoming a member'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {ptaData?.shared_email && (
                  <Button size="lg" asChild>
                    <a href={`mailto:${ptaData.shared_email}`}>
                      Contact Us
                    </a>
                  </Button>
                )}
                <Link to={`/fundraising/${ptaId}`}>
                  <Button size="lg" variant="outline">
                    Back to Fundraising Page
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-card border-t border-border mt-auto">
        <div className="container py-8">
          <div className="border-t border-border pt-8 text-center text-sm text-foreground">
            <p>© {new Date().getFullYear()} The PTA Hub. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}